import { useState } from "react";
import { IBidArea } from "@miplatform/types";
import { getItemLocal, setItemLocal } from "@miplatform/utils";
import { useCreateBidContext } from "../../../CreateBidContext";

export const useBidAreaViewModel = () => {
  const { form } = useCreateBidContext();
  const { setValue, watch } = form;
  const { planAreas, id: bidId } = watch();
  const initDismissed = getItemLocal(bidId) ?? {};

  const [dismissedCategories, setDismissedCategories] = useState(initDismissed);

  const handleRemoveItemFromArea = (area: IBidArea, materialId: string) => {
    const updatedAreas = planAreas.map((a) => {
      if (a.id === area.id) {
        return {
          ...a,
          materials: a.materials?.filter((m) => m.materialId !== materialId),
        };
      }
      return a;
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });
  };

  const handleUpdateItemQuantity = (
    area: IBidArea,
    materialId: string,
    quantity: number
  ) => {
    const updatedAreas = planAreas.map((a) => {
      if (a.id === area.id) {
        return {
          ...a,
          materials: a.materials?.map((m) =>
            m.materialId === materialId ? { ...m, quantity } : m
          ),
        };
      }
      return a;
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });
  };

  const handleRemoveSuppliedByBuilder = (
    area: IBidArea,
    materialId: string
  ) => {
    const updatedAreas = planAreas.map((a) => {
      if (a.id === area.id) {
        return {
          ...a,
          materials: a.materials?.map((m) =>
            m.materialId === materialId ? { ...m, suppliedByBuilder: false } : m
          ),
        };
      }
      return a;
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });
  };

  const handleRemoveArea = (area: IBidArea) => {
    const updatedAreas = planAreas.filter((a) => a.id !== area.id);
    setValue("planAreas", updatedAreas, { shouldDirty: true });
  };

  const handleUpdateAreaLabel = (area: IBidArea, label: string) => {
    const updatedAreas = planAreas.map((a) => {
      if (a.id === area.id) {
        return { ...a, label };
      }
      return a;
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });
  };

  const handleMoveArea = (area: IBidArea, direction: "up" | "down") => {
    const { floorId, specColumn } = area;

    // Get areas in same column and floor, sorted by displayOrder
    const columnAreas = planAreas
      .filter((a) => a.floorId === floorId && a.specColumn === specColumn)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const currentIndex = columnAreas.findIndex((a) => a.id === area.id);

    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= columnAreas.length) return;

    // Swap displayOrder values
    const updatedColumnAreas = [...columnAreas];
    [updatedColumnAreas[currentIndex], updatedColumnAreas[targetIndex]] = [
      updatedColumnAreas[targetIndex],
      updatedColumnAreas[currentIndex],
    ];

    // Reassign displayOrder based on new position
    const reorderedColumnAreas = updatedColumnAreas.map((a, idx) => ({
      ...a,
      displayOrder: idx,
    }));

    // Rebuild planAreas in-place: update displayOrder for this column's areas
    const updatedAreas = planAreas.map((a) => {
      if (a.floorId === floorId && a.specColumn === specColumn) {
        const reordered = reorderedColumnAreas.find((r) => r.id === a.id);
        return reordered || a;
      }
      return a;
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });
  };

  const handleDismissCategory = (categoryId: string, areaId: string) => {
    if (!areaId) return;
    const modifiedDismissedCategories = getItemLocal(bidId) || {};

    if (modifiedDismissedCategories[areaId]) {
      modifiedDismissedCategories[areaId].push(categoryId);
    } else {
      modifiedDismissedCategories[areaId] = [categoryId];
    }

    setDismissedCategories(modifiedDismissedCategories);
    setItemLocal(bidId, modifiedDismissedCategories);
  };

  return {
    dismissedCategories,
    handleRemoveItemFromArea,
    handleUpdateItemQuantity,
    handleRemoveSuppliedByBuilder,
    handleRemoveArea,
    handleUpdateAreaLabel,
    handleMoveArea,
    handleDismissCategory,
  };
};
